import { useMemo } from 'react';
import { Coins, Plus, Minus } from 'lucide-react';
import { Slider } from '../ui/slider';

export function TokenPurchaseCard({ 
  track, 
  tokenInfo, 
  tokenAmount, 
  setTokenAmount, 
  addingToCart, 
  onAddTokensToCart 
}) {
  return (
    <div className="glass-card token-card p-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-cyan-500 to-purple-500 flex items-center justify-center">
          <Coins className="w-5 h-5 text-white" />
        </div>
        <div>
          <h2 className="text-xl font-bold">Fractional Ownership</h2>
          <p className="text-sm text-gray-400">Own a piece of this track</p>
        </div>
      </div>

      {/* Token Stats */}
      <div className="grid grid-cols-2 gap-4 mb-6">
        <div className="bg-white/5 rounded-lg p-4">
          <p className="text-sm text-gray-400 mb-1">Price per 1%</p>
          <p className="text-xl font-bold token-data">${track.token_price.toFixed(2)}</p>
        </div>
        <div className="bg-white/5 rounded-lg p-4">
          <p className="text-sm text-gray-400 mb-1">Available</p>
          <p className="text-xl font-bold text-purple-400">{tokenInfo?.available_tokens}%</p>
        </div>
      </div>

      {/* Progress */}
      <TokenProgress availableTokens={tokenInfo?.available_tokens} />

      {/* Token Amount Selector */}
      <TokenAmountSelector 
        tokenAmount={tokenAmount}
        setTokenAmount={setTokenAmount}
        maxTokens={tokenInfo?.available_tokens || 1}
      />

      {/* Total */}
      <div className="bg-white/5 rounded-lg p-4 mb-6">
        <div className="flex justify-between items-center">
          <span className="text-gray-400">Total Cost</span>
          <span className="text-2xl font-bold token-data">
            ${(tokenAmount * track.token_price).toFixed(2)}
          </span>
        </div>
      </div>

      {/* Buy Button */}
      <button
        onClick={onAddTokensToCart}
        disabled={addingToCart || tokenInfo?.available_tokens === 0}
        className="w-full btn-secondary flex items-center justify-center gap-2 py-4 text-lg neon-glow-cyan"
        data-testid="add-tokens-to-cart"
      >
        <Coins className="w-5 h-5" />
        Add {tokenAmount}% Ownership to Cart
      </button>

      {tokenInfo?.available_tokens === 0 && (
        <p className="text-center text-red-400 text-sm mt-4">
          All tokens have been sold
        </p>
      )}
    </div>
  );
}

export function TokenProgress({ availableTokens }) {
  const soldPercentage = 100 - (availableTokens || 0);
  
  return (
    <div className="mb-6">
      <div className="flex justify-between text-sm mb-2">
        <span className="text-gray-400">Ownership Sold</span>
        <span className="token-data">{soldPercentage}%</span>
      </div>
      <div className="h-3 bg-white/10 rounded-full overflow-hidden">
        <div 
          className="h-full bg-gradient-to-r from-purple-500 to-cyan-500"
          style={{ width: `${soldPercentage}%` }}
        />
      </div>
    </div>
  );
}

export function TokenAmountSelector({ tokenAmount, setTokenAmount, maxTokens }) {
  // Memoize the slider value array to prevent unnecessary re-renders
  const sliderValue = useMemo(() => [tokenAmount], [tokenAmount]);
  const sliderMax = useMemo(() => Math.min(maxTokens, 50), [maxTokens]);
  
  return (
    <div className="mb-6">
      <p className="text-sm text-gray-400 mb-3">Select Amount (%)</p>
      <div className="flex items-center gap-4 mb-4">
        <button
          onClick={() => setTokenAmount(Math.max(1, tokenAmount - 1))}
          className="w-10 h-10 rounded-lg bg-white/5 flex items-center justify-center hover:bg-white/10"
          data-testid="decrease-token"
        >
          <Minus className="w-4 h-4" />
        </button>
        <div className="flex-1">
          <Slider
            value={sliderValue}
            min={1}
            max={sliderMax}
            step={1}
            onValueChange={([val]) => setTokenAmount(val)}
            className="w-full"
            data-testid="token-slider"
          />
        </div>
        <button
          onClick={() => setTokenAmount(Math.min(maxTokens, tokenAmount + 1))}
          className="w-10 h-10 rounded-lg bg-white/5 flex items-center justify-center hover:bg-white/10"
          data-testid="increase-token"
        >
          <Plus className="w-4 h-4" />
        </button>
      </div>
      <div className="text-center">
        <span className="text-3xl font-bold gradient-text">{tokenAmount}%</span>
        <span className="text-gray-400 ml-2">ownership</span>
      </div>
    </div>
  );
}

export function TokenInfoBox() {
  return (
    <div className="mt-6 p-4 border border-white/10 rounded-lg">
      <p className="text-sm text-gray-400">
        <strong className="text-white">What you get:</strong> Fractional ownership entitles you to a 
        percentage of this track's royalty earnings. As the track generates revenue from streams, 
        sales, and licensing, you'll receive proportional payouts.
      </p>
    </div>
  );
}
